/** Панель инструментов списка. */
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {DxGridToolbarOptions} from "../../../domain/ui-options/toolbar/dx-grid-toolbar-options";
import {Subject, Subscription} from "rxjs";
import {DxGridState} from "../../../domain/dx-grid-state";
import {DxGridToolbarButton} from "../../../domain/ui-options/toolbar/dx-grid-toolbar-button";
import {DxGridRow} from "../../../domain/dx-grid-row";
import {DxGridToolbarAction} from "../../../domain/ui-options/toolbar/dx-grid-toolbar-action";
import {DxGridToolbarActionType} from "../../../domain/ui-options/toolbar/dx-grid-toolbar-action-type";


@Component({
  selector: 'dx-grid-toolbar',
  templateUrl: './dx-grid-toolbar.component.html',
  styleUrls: ['./dx-grid-toolbar.component.css']
})
export class DxGridToolbarComponent implements OnInit, OnDestroy {
  /** Опции панели инструментов грида. */
  @Input()
  public toolbarOptions: DxGridToolbarOptions;

  /** Обсервэбл объект на состояния грида. */
  private _gridState: Subject<DxGridState>;
  /** Кнопки панели инструментов грида. */
  private _buttons: DxGridToolbarButton[];
  /** Подписка для корректного очищения */
  private _subscription: Subscription;
  /** Мапа для расширения множества резолверов действий на тулбаре индивидуально для каждой строки. */
  protected _toolbarActionResolvers: Map<string, (item: DxGridRow, action: DxGridToolbarAction) => boolean>;

  /** Событие выбрасывается при нажатии кнопки на панели инструментов. */
  @Output()
  public action: EventEmitter<DxGridToolbarAction> = new EventEmitter();

  constructor() {
    this._buttons = [];
    this._toolbarActionResolvers = new Map();
  }

  @Input()
  set gridState(value: Subject<DxGridState>) {
    this._gridState = value;
  }

  get buttons(): DxGridToolbarButton[] {
    return this._buttons;
  }


  ngOnInit(): void {
    this._subscription = this._gridState.subscribe(state => {
      this._updateToolbar(state);
    });
    if(this.toolbarOptions.buttons) {
      this._buttons = [...this.toolbarOptions.buttons];
    }
  }

  ngOnDestroy(): void {
    if(this._subscription) this._subscription.unsubscribe();
    this._toolbarActionResolvers.clear();
  }


  public onToolbarBtnClick(btn: DxGridToolbarButton): void {
    this.action.emit(btn.action);
  }


  /**
   * Добавляет резолвер действия на панели инструментов, позволяющий определить доступность действия
   * при помощи произваольного алгоритма на основании данных строки и типа предполагаемого действия.
   * @param {string} key - ключ функции резолвера в мапе.
   * @param {(row: DxGridRow, action: DxGridToolbarAction) => boolean} resolver - функция резолвел.
   * @return {boolean} - true - дейтвие разрешено, false - запрещено.
   */
  public addActionResolver(key: string, resolver: (row: DxGridRow, action: DxGridToolbarAction) => boolean): void {
    this._toolbarActionResolvers.set(key,  resolver);
  }


  /**
   * Удаляет резолвер действия на панели инструментов по его ключу.
   * @param {string} key - ключ функции резолвера в мапе.
   */
  public removeActionResolver(key: string): boolean {
    if(this._toolbarActionResolvers.has(key)) {
      this._toolbarActionResolvers.delete(key);
      return true;
    }
    return false;
  }


  /** Возвращает css-класс для кнопки панели инструментов. */
  public getBtnClass(btn: DxGridToolbarButton): string {
    return '';
  }


  /**
   * Обновляет тулбар в зависимости от состояния списка.
   * Статически (то есть на этапе компиляции) в `_updateToolbar(...)` выполняются проверки только на основании
   * состава филдов `DxGridState`. Все дополнительные проверки, специфичные для той или иной реализации списка
   * должны добавляться в процессе выполнения через регистрации резолверов.
   *
   * Смысл в том что есть проверки по площадям (наличие выделенных, удаленных, обновленных и тд элементов).
   * То есть некоего подможества множества элементов. А дальше уже в рамках элементов этого подмножества запускаются
   * индивидуальные проверки, связанные с данными элемента или какими-либо еще действиями над элементом.
   *
   * @param {DxGridState} state - снапшот текущего состояния грида.
   */
  private _updateToolbar(state: DxGridState): void {
    if(state.selectedRows.length > 0) {
      this._buttons
        .filter(btn => (btn.action.isSingleRowAction && !btn.action.isSelectionIndependent))
        .forEach(btn => {
          btn.disabled = (state.selectedRows.length > 1
                            || !this._isToolbarActionEnabled(state.selectedRows, btn.action));
        });

      this._buttons
        .filter(btn => (btn.action.isMultiRowAction && !btn.action.isSelectionIndependent))
        .forEach(btn => {
          btn.disabled = !this._isToolbarActionEnabled(state.selectedRows, btn.action);
        });

      this._buttons
        .filter(btn => btn.action.isSelectionDependent)
        .forEach(btn => {
          btn.disabled = !this._isToolbarActionEnabled(state.selectedRows, btn.action);
        });
    }
    // Если выделение пустое то лочим и синг и мульти действия.
    // Оставляем только те, которые не зависят от наличия выделения.
    else {
      this._buttons
        .filter(btn => (btn.action.isSingleRowAction || btn.action.isMultiRowAction))
        .forEach(item => item.disabled = true);

      this._buttons
        .filter(btn => btn.action.isSelectionDependent)
        .forEach(item => {
          item.disabled = false;
        });
    }

    if(state.updatedRows.length > 0) {
      this._buttons
        .filter(btn => btn.action.type == DxGridToolbarActionType.SAVE)
        .forEach(btn => btn.disabled = false);
    } else {
      this._buttons
        .filter(btn => btn.action.type == DxGridToolbarActionType.SAVE)
        .forEach(btn => btn.disabled = true);
    }

    if(state.removedRows.length) {}

    if(state.hasFilter) {
      this._buttons
        .filter(btn => btn.action.type == DxGridToolbarActionType.FLUSH_FILTERS)
        .forEach(btn => btn.disabled = false);
    } else {
      this._buttons
        .filter(btn => btn.action.type == DxGridToolbarActionType.FLUSH_FILTERS)
        .forEach(btn => btn.disabled = true);
    }

    if(state.rowsCount > 0) {
      this._buttons
        .filter(btn => btn.action.isMultiRowAction)
        .forEach(item => item.disabled = false);
    } else {
      this._buttons
        .filter(btn => btn.action.isMultiRowAction)
        .forEach(item => item.disabled = true);
    }
  }


  /**
   * Определяет доступность кнопки панели инструментов для конкретных выбранных элементов списка.
   * @param {DxListItem[]} items - текущие подмножество элементов.
   * @param {DxListToolbarAction} action - действие на панели инструментов.
   * @return {boolean} - true - действие может быть выполненео, false - нет
   */
  private _isToolbarActionEnabled(items: DxGridRow[], action: DxGridToolbarAction): boolean {
    // Для каждого элемента подмножества запускаем индивидуальные проверки.
    let counter = 0;
    let isAllowed: boolean = true;
    items.forEach(item => {
      // Проверка возможности выполнения действия сугубо на освании значений филдов дата-провайдера элемента.
      // TODO: Подумать как можно переименовать функцию чтоб название отражало суть.
      isAllowed = isAllowed && item.canExecuteToolbarAction(action);

      // Если зареганы дополнительные резолверы, то прогоняем их.
      let resolver: (items: DxGridRow, action: DxGridToolbarAction) => boolean;
      this._toolbarActionResolvers.forEach((value, key) => {
        resolver = value; // Просто для читаемости
        isAllowed = isAllowed && resolver(item, action);
      });

      if (isAllowed) { counter++; }
    });
    return items.length === counter;
  }
}
